/**
 * `useActivitySnapshot` — renderer-side subscriber for the
 * `activity:state` push channel (umbrella design
 * `docs/agent-transparency-design.md`, C2 plan
 * `docs/c2-transparency-banner-design.md` §10).
 *
 * Returns the latest `ActivitySnapshot` for the given `gameId`, or
 * `null` when no build is in flight for this game. Subscribes on
 * mount (and on every `gameId` change), unsubscribes on unmount and
 * on `gameId` change.
 *
 * Wire shape — see `ActivitySnapshot` / `ActivityStatePush` /
 * `AgentActivity` in `../../shared/ipc-channels.ts`. This hook orders
 * opaque session strings by `ActivitySnapshot.sessionCreatedAt` — the
 * authoritative first-appearance timestamp stamped in main (Crucible
 * #679). That closes the residual cross-session race the earlier
 * renderer-local first-seen `Map` only mitigated: ordering no longer
 * depends on which session's first push the renderer happened to
 * observe first. `sessionCreatedAt` is a required field produced by
 * the same release that runs this hook (main + renderer ship in one
 * Electron bundle), so there is no missing-field migration path to
 * handle — it is always present.
 *
 * Discard rules (Codex round-1 HIGH on the original design draft —
 * blindly accepting any push with a different `sessionId` is unsafe
 * because a stale session's late tail-push could clobber a newer
 * session's banner; blindly accepting `snapshot: null` is unsafe for
 * the same reason):
 *
 *   1. Cross-game pushes (`push.gameId !== gameId`) are dropped
 *      silently — the same `crucible` instance can have multiple
 *      games' registries active in main and the renderer must filter
 *      defensively even though the route currently mounts one game
 *      at a time.
 *   2. `snapshot: null` for a session that has not yet been the
 *      active session is a no-op (idempotent) — there is nothing to
 *      clear. Once there is an active session, the next null clears
 *      it.
 *   3. Same-session pushes follow `(activeTurnId, seq)` tuple
 *      ordering: a strictly older `activeTurnId` is dropped; same
 *      `activeTurnId` with `seq <=` latest is dropped.
 *   4. Cross-session pushes are accepted only when the incoming
 *      session's `sessionCreatedAt` is strictly greater than the
 *      active session's. A stale session's late tail-push (older or
 *      equal createdAt) is dropped regardless of arrival order.
 *      Because `sessionCreatedAt` is stamped authoritatively in main
 *      (Crucible #679), this holds even when the active session's own
 *      first push never reached the renderer before this one — the
 *      case the removed first-seen `Map` could not handle.
 *
 * Workspace-side reinforcement (belt-and-braces): the centre-column
 * shell that hosts this hook mounts with `key={gameId}` so the
 * effect remount on game navigation drops any in-flight state. The
 * `useEffect` cleanup below is the load-bearing path; the key reset
 * is the second seatbelt.
 */

import { useEffect, useRef, useState } from 'react';
import type { ActivitySnapshot, ActivityStatePush } from '@volley/crucible-ipc-types/ipc-channels';
import { useCrucibleClient } from '../transport/context';

export function useActivitySnapshot(gameId: string): ActivitySnapshot | null {
  const client = useCrucibleClient();
  const [snapshot, setSnapshot] = useState<ActivitySnapshot | null>(null);
  const activeSessionId = useRef<string | null>(null);
  const activeSessionCreatedAt = useRef<number | null>(null);
  const latestTuple = useRef<{
    sessionId: string;
    activeTurnId: number;
    seq: number;
  } | null>(null);

  useEffect(() => {
    // Reset all refs + state for the new gameId. Belt-and-braces with
    // the Workspace-side `key={gameId}` remount, but load-bearing on
    // its own — if a caller forgets the key, the cleanup still drops
    // every cross-game leak.
    activeSessionId.current = null;
    activeSessionCreatedAt.current = null;
    latestTuple.current = null;
    setSnapshot(null);

    const unsubscribe = client.subscribe('activity:state', (push: ActivityStatePush) => {
      // Cross-game guard — drop silently. The same renderer can
      // observe pushes for any game whose registry is alive in
      // main; the hook MUST filter on the local `gameId` closure.
      if (push.gameId !== gameId) return;

      const next = push.snapshot;

      // Null-snapshot rule. A null carries no tuple, only a
      // gameId. Treat it as terminal-for-current-session ONLY when
      // there is an active session to clear; otherwise it is a
      // no-op (idempotent). Stale tails of a previous session
      // would otherwise unmount a valid banner.
      if (next === null) {
        if (activeSessionId.current !== null) {
          activeSessionId.current = null;
          activeSessionCreatedAt.current = null;
          latestTuple.current = null;
          setSnapshot(null);
        }
        return;
      }

      // Cross-session discard (Crucible #679). If we already have an
      // active session AND the incoming push is for a different
      // session, accept it only when the incoming session was created
      // AFTER the active session. Ordering reads the authoritative
      // `sessionCreatedAt` stamped in main, so a stale session's late
      // tail-push (older-or-equal createdAt) is dropped regardless of
      // arrival order — even if the active session's own first push
      // never reached this renderer before the stale tail arrived.
      if (activeSessionId.current !== null && activeSessionId.current !== next.sessionId) {
        if (
          activeSessionCreatedAt.current !== null &&
          next.sessionCreatedAt <= activeSessionCreatedAt.current
        ) {
          // The "different" session is actually older (or a
          // same-instant tie). Drop.
          return;
        }
        // The incoming session is newer. Roll forward — clear the
        // tuple cache so the new session's first push is accepted
        // by the tuple check below.
        activeSessionId.current = next.sessionId;
        activeSessionCreatedAt.current = next.sessionCreatedAt;
        latestTuple.current = null;
      }

      // First push for this hook's lifetime — adopt this sessionId
      // as active.
      if (activeSessionId.current === null) {
        activeSessionId.current = next.sessionId;
        activeSessionCreatedAt.current = next.sessionCreatedAt;
      }

      // Within-session tuple ordering. See umbrella design §2
      // "Push cadence". `seq` resets to 0 on every `activeTurnId`
      // advance, so the comparison is two-stage.
      const cur = latestTuple.current;
      if (cur && cur.sessionId === next.sessionId) {
        if (next.activeTurnId < cur.activeTurnId) return;
        if (next.activeTurnId === cur.activeTurnId && next.seq <= cur.seq) {
          return;
        }
      }
      latestTuple.current = {
        sessionId: next.sessionId,
        activeTurnId: next.activeTurnId,
        seq: next.seq,
      };
      setSnapshot(next);
    });

    return unsubscribe;
  }, [client, gameId]);

  return snapshot;
}
